<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      最終確認
    </Header>

    <div class="mb-5 pb-5 bg-white">
      <dl class="px-3 pb-3 text-size-14">
        <dt class="pt-3 text-weight-500">雇用主</dt>
        <dd class="text-mild">{{ orderDetail.office.name }}</dd>

        <dt class="pt-3 text-weight-500">被用者</dt>
        <dd class="text-mild">{{ `${user.lastName} ${user.firstName}` }}</dd>

        <dt class="pt-3 text-weight-500">契約形態</dt>
        <dd class="text-mild">雇用契約(労災保険の対象)</dd>

        <dt class="pt-3 text-weight-500">雇用契約</dt>
        <dd class="text-mild">
          勤務開始の1時間前に改めて労働条件通知書が送付され、その時点で雇用契約締結となります。
        </dd>

        <dt class="pt-3 text-weight-500">勤務地</dt>
        <dd class="text-mild">{{ orderDetail.address }}</dd>

        <dt class="pt-3 text-weight-500">業務日時</dt>
        <dd class="text-mild">
          {{ formattedDate }}
          {{ formattedWorkTimeRange }}
        </dd>

        <dt class="pt-3 text-weight-500">業務報酬</dt>
        <dd class="text-mild">
          {{ salaryPerDayWithVariousAllowances }}円
          <span
            v-if="orderDetail.variousAllowances > 0"
            class="text-mild"
            data-testid="various-allowances"
          >
            (交通費{{ orderDetail.variousAllowances.toLocaleString() }}円込み)
          </span>
        </dd>

        <dt class="pt-3 text-weight-500">前日確認</dt>
        <dd class="text-mild">
          勤務の可否を確認するため、お仕事の前日にアプリを起動し、「勤務確定」をお願いします。<br />
          前日に「勤務確定」されない場合、お仕事がキャンセルされる可能性がございますので、ご注意ください。
        </dd>

        <dt class="pt-3 text-weight-500">出退勤</dt>
        <dd class="text-mild">
          業務当日に、業務先のQRコードを「仕事管理」からQRコードリーダーで読み取り、出退勤してください。
        </dd>

        <dt class="pt-3 text-weight-500">報酬受取</dt>
        <dd>
          <RewardDescription />
        </dd>
      </dl>

      <OrderFixedButton
        :isDisabled="isExecuting"
        :abTestGroup3300="abTestGroup3300"
        enabledVariant="primary"
        @click="$bvModal.show('apply-datetime-confirm')"
      >
        申し込み完了
      </OrderFixedButton>
    </div>
    <BModal
      id="apply-datetime-confirm"
      title="業務日時に間違いはありませんか？"
      header-class="font-weight-bold modal-responsive-title"
      cancel-title="キャンセル"
      cancel-variant="gray-03"
      @ok="onClickApply"
      centered
    >
      <div class="modal-responsive-medium text-danger">
        ⚠応募ミスのキャンセルも信用スコアが減点されます
      </div>
      <br />
      <div class="modal-responsive-large"><u>業務日時</u></div>
      <div class="modal-responsive-large">
        {{ formattedDate }}
        {{ formattedWorkTimeRange }}
      </div>
    </BModal>
  </div>
</template>

<script lang="ts">
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import OrderFixedButton from "@/components/organisms/orders/OrderFixedButton.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { authStore } from "@/store/auth";
import { orderStore } from "@/store/order";
import { showErrorModal } from "@/utils/showErrorModal";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { Component, Vue } from "vue-facing-decorator";
import { orderService } from "@/app/services/OrderService";
import { User } from "@/types/user/User";
import { OrderDetail } from "@/types/order/Order";
import { routerPush } from "@/utils/routerPush";
import { OrderWorkTimeOption } from "@/types/order/OrderWorkTimeOption";
import {
  AbTest3300,
  AbTest2807,
  AbTest3113,
  AbTest3401
} from "@/app/services/AbTestService";
import RewardDescription from "@/components/organisms/order/RewardDescription.vue";
import { salaryService } from "@/app/services/SalaryService";

@Component({
  components: {
    FeatherIcon,
    Header,
    OrderFixedButton,
    RewardDescription
  }
})
export default class OrderLastConfirmation extends Vue {
  isExecuting = false; // 2度押し防止用

  get orderId(): number {
    return Number(this.$route.params["orderId"]);
  }
  get user(): User {
    return authStore.user!;
  }
  get orderDetail(): OrderDetail {
    return orderStore.orderDetail!;
  }
  get formattedDate(): string {
    return orderService.getFormattedDateWithYear(this.orderDetail);
  }
  get workTimeOptionId(): number {
    return Number(this.$route.params["workTimeOptionId"]);
  }
  get formattedWorkTimeRange(): string {
    return orderService.getFormattedWorkTimeRangeFromWorkTimeOption(
      this.workTimeOption
    );
  }
  get workTimeOption(): OrderWorkTimeOption {
    return this.orderDetail.workTimeOptions.find(
      w => w.id === this.workTimeOptionId
    )!;
  }
  get salaryPerDayWithVariousAllowances() {
    return (
      this.workTimeOption.salaryPerDay + this.orderDetail.variousAllowances
    ).toLocaleString();
  }
  get abTestGroup3300(): "B" | "C" {
    return AbTest3300.getTestGroup();
  }

  async created() {
    await salaryService.fetchHasBankAccount();
  }

  async onClickApply() {
    try {
      this.$loading.show();
      this.isExecuting = true;

      const application = await applicationService.createApplication({
        orderId: this.orderDetail.id,
        orderVersion: this.orderDetail.version,
        workTimeOptionId: this.workTimeOptionId
      });

      sendAdjustEvent("FINISH_APPLICATION");
      sendFirebaseCustomEvent("finish_application", {
        order_id: this.orderDetail.id
      });

      AbTest3401.registerEvent(AbTest3401.eventTypes.completeApplication);
      AbTest3113.registerEvent(AbTest3113.eventTypes.completeApplication);
      AbTest2807.registerEvent(AbTest2807.eventTypes.completeApplication);
      AbTest3300.registerEvent(AbTest3300.eventTypes.completeApplication);

      // 自分が応募したものは後で見るから削除
      orderStore.removeFromWatchLaterOrderList(this.orderId);

      await routerPush(this, {
        name: "OrdersSendMessage",
        params: {
          applicationId: application.id
        }
      });
    } catch (error) {
      await showErrorModal(this, error as Error);
      routerPush(this, {
        name: "Search",
        params: { applicationFailed: "true" }
      });
    } finally {
      this.$loading.hide();
      this.isExecuting = false;
    }
  }
}
</script>

<style>
.modal-responsive-title {
  font-size: 4.5vw;
}

.modal-responsive-large {
  font-size: 6vw;
}

.modal-responsive-medium {
  font-size: 3.6vw;
}

/* modal が横幅全体に張り付く上限サイズ 576px (X-Small) を境にフォントサイズをレスポンシブに切り替える */
@media (min-width: 576px) {
  .modal-responsive-title {
    font-size: 19pt;
  }

  .modal-responsive-large {
    font-size: 26pt;
  }

  .modal-responsive-medium {
    font-size: 14pt;
  }
}
</style>
